/**
 * Share with My Provider
 *
 * Generates a clean, single-page (or two-page) clinical-friendly summary a
 * sister can hand to her OB, midwife, or registered dietitian. The page
 * renders on screen with a visible "Print or save as PDF" button; print
 * CSS in index.css hides the phone shell and reveals only the document.
 *
 * Design notes:
 *   - Wellness guidance, not medical prescription. Disclaimers at the
 *     top and the bottom are non-negotiable.
 *   - We surface season, plan, 14-day adherence, macro distribution, and
 *     the season-specific micronutrient panel because that is what a
 *     clinician will actually scan in 60 seconds.
 *   - No em-dashes in user-facing copy.
 *   - Print uses A4 portrait so it feels like a real document, not a
 *     mobile screenshot.
 */
import { Link } from "wouter";
import { useStoreQuery, queries, MICRONUTRIENT_PANELS, type Tier } from "@/lib/store";
import { Btn, GlassCard, PillTag } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";

/* ---------------------------- Helpers ---------------------------- */

function fmtDate(ts: number, opts?: Intl.DateTimeFormatOptions): string {
  return new Date(ts).toLocaleDateString(undefined, opts ?? { year: "numeric", month: "long", day: "numeric" });
}

function fmtDateShort(date: string): string {
  /* date is YYYY-MM-DD (local). Render as "Apr 26" without timezone shift. */
  const [_y, m, d] = date.split("-").map((s) => parseInt(s, 10));
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[m - 1]} ${d}`;
}

function dayOfWeekLabel(date: string): string {
  /* Render as "Sun" without TZ drift, by anchoring to noon local time. */
  const [y, m, d] = date.split("-").map((s) => parseInt(s, 10));
  const dt = new Date(y, m - 1, d, 12, 0, 0);
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dt.getDay()];
}

function seasonLabel(kind: string, trimester: number | null, weeksPostpartum: number | null, feeding: string): string {
  switch (kind) {
    case "ttc": return "Trying to conceive";
    case "pregnant": return `Pregnant${trimester ? `, trimester ${trimester}` : ""}`;
    case "postpartum": return `Postpartum${weeksPostpartum != null ? `, week ${weeksPostpartum}` : ""}`;
    case "nursing": return `Nursing${feeding === "exclusive" ? ", exclusive" : feeding === "mixed" ? ", mixed feeding" : ""}`;
    default: return "General wellness, no perinatal season";
  }
}

function tierLabel(tier: Tier): string {
  return `${tier} tier`;
}

/* ---------------------------- Print document ---------------------------- */

function ProviderDocument({ userId }: { userId: string }) {
  const data = useStoreQuery(() => queries.providerExportData(userId));
  if (!data) return null;
  const { user, plan, profile, season, panelKey, recent14, summary, generatedAt } = data;
  const panel = MICRONUTRIENT_PANELS[panelKey];

  /* Window of dates printed in the 14-day table. Oldest first because that
   * is how a clinician scans a chart. */
  const days = recent14;

  /* Macro distribution pie data. If a sister has no plan, we show actual
   * average distribution so the page is still useful. */
  const distSource = plan
    ? { p: plan.proteinG * 4, c: plan.carbsG * 4, f: plan.fatG * 9 }
    : { p: summary.avgProtein * 4, c: summary.avgCarbs * 4, f: summary.avgFat * 9 };
  const distTotal = Math.max(1, distSource.p + distSource.c + distSource.f);
  const dist = {
    p: Math.round((distSource.p / distTotal) * 100),
    c: Math.round((distSource.c / distTotal) * 100),
    f: Math.round((distSource.f / distTotal) * 100),
  };

  return (
    <div
      data-print-root
      className="provider-doc"
      style={{
        background: "#ffffff",
        color: "#1a1a1a",
        fontFamily: "var(--font-body, 'Raleway', sans-serif)",
        padding: "32px",
        maxWidth: 720,
        margin: "0 auto",
        borderRadius: 16,
        boxShadow: "0 4px 24px rgba(30,74,53,0.10)",
      }}
      data-testid="provider-document"
    >
      {/* Letterhead */}
      <header data-print-keep style={{ borderBottom: "2px solid #1E4A35", paddingBottom: 16, marginBottom: 20 }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="mnf-eyebrow" style={{ color: "#1E4A35", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700 }}>
              Move Nourish Flourish
            </p>
            <h1
              className="mnf-serif"
              style={{ fontSize: 24, fontWeight: 500, color: "#163828", lineHeight: 1.2, marginTop: 6, letterSpacing: "-0.01em" }}
            >
              Wellness summary for my provider
            </h1>
            <p className="mnf-body" style={{ fontSize: 11, color: "#6B7B6F", marginTop: 6 }}>
              Generated {fmtDate(generatedAt, { year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            <p className="mnf-eyebrow" style={{ color: "#A07820", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700 }}>
              GGS Pre/Postnatal V2
            </p>
            <p className="mnf-body" style={{ fontSize: 10.5, color: "#6B7B6F", marginTop: 4 }}>
              Coach Kiley Mitchell
            </p>
            <p className="mnf-body" style={{ fontSize: 10.5, color: "#6B7B6F" }}>
              kiley@movenourishflourish.app
            </p>
          </div>
        </div>
      </header>

      {/* Top disclaimer */}
      <div
        data-print-keep
        style={{
          background: "#FAF6EE",
          border: "1px solid rgba(30,74,53,0.12)",
          borderRadius: 8,
          padding: "10px 14px",
          marginBottom: 20,
        }}
      >
        <p className="mnf-body" style={{ fontSize: 10.5, color: "#2D3E30", lineHeight: 1.5, fontStyle: "italic" }}>
          This is a wellness summary, not a medical record. The macro and movement guidance shown here is
          coach-paced support, never a clinical prescription. Please weigh anything below against your own
          clinical judgment for {user.name}.
        </p>
      </div>

      {/* Sister identity + season */}
      <section data-print-keep style={{ marginBottom: 20 }}>
        <h2 className="mnf-serif" style={{ fontSize: 14, fontWeight: 700, color: "#163828", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
          About this sister
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
          <Field label="Name" value={user.name} />
          <Field label="Email" value={user.email} />
          <Field label="Account tier" value={tierLabel(user.tier)} />
          <Field label="Member since" value={fmtDate(user.createdAt)} />
          {profile && <Field label="Age" value={`${profile.ageYears} years`} />}
          {profile && <Field label="Height" value={`${Math.floor(profile.heightIn / 12)} ft ${profile.heightIn % 12} in`} />}
          {profile && <Field label="Weight" value={`${profile.weightLb} lb`} />}
          {profile && <Field label="Activity" value={profile.activity.replace(/-/g, " ")} />}
          {profile && <Field label="Goal direction" value={profile.direction} />}
          {profile && <Field label="Goal pace" value={profile.pace} />}
        </div>
      </section>

      {/* Season + cleared for movement */}
      <section data-print-keep style={{ marginBottom: 20 }}>
        <h2 className="mnf-serif" style={{ fontSize: 14, fontWeight: 700, color: "#163828", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
          Current season
        </h2>
        <div
          style={{
            border: "1px solid rgba(30,74,53,0.12)",
            borderRadius: 10,
            padding: 14,
            background: "#FAF6EE",
          }}
        >
          <p className="mnf-serif" style={{ fontSize: 16, fontWeight: 500, color: "#163828" }}>
            {season ? seasonLabel(season.kind, season.trimester, season.weeksPostpartum, season.feeding) : "General wellness"}
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", marginTop: 10 }}>
            {season?.milestoneDate && (
              <Field
                label={season.kind === "pregnant" ? "Estimated due date" : season.kind === "postpartum" ? "Birth date" : "Milestone date"}
                value={season.milestoneDate}
              />
            )}
            {season && (
              <Field
                label="Cleared for movement"
                value={season.clearedForMovement ? "Yes" : "Awaiting clearance"}
              />
            )}
            {season && <Field label="Feeding" value={season.feeding === "none" ? "n/a" : season.feeding} />}
            {season && <Field label="Season updated" value={fmtDate(season.updatedAt)} />}
          </div>
          <p className="mnf-body" style={{ fontSize: 10.5, color: "#6B7B6F", marginTop: 10, lineHeight: 1.5, fontStyle: "italic" }}>
            Postpartum movement clearance is gated by the sister's OB or midwife. MNF defers to your clinical
            decision and never overrides it.
          </p>
        </div>
      </section>

      {/* Daily targets */}
      <section data-print-keep style={{ marginBottom: 20 }}>
        <h2 className="mnf-serif" style={{ fontSize: 14, fontWeight: 700, color: "#163828", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
          Daily nutrition targets
        </h2>
        {plan ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              <TargetCell label="Energy" value={plan.kcal.toLocaleString()} unit="kcal" accent="#1E4A35" />
              <TargetCell label="Protein" value={plan.proteinG.toString()} unit="g" accent="#1E4A35" />
              <TargetCell label="Carbohydrate" value={plan.carbsG.toString()} unit="g" accent="#A07820" />
              <TargetCell label="Fat" value={plan.fatG.toString()} unit="g" accent="#C0784A" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", marginTop: 12 }}>
              <Field label="Macro distribution" value={`${dist.p}P / ${dist.c}C / ${dist.f}F`} />
              <Field label="Plan source" value={plan.source === "coach" ? "Coach Kiley (locked)" : "Self-directed"} />
              <Field label="Mode" value={plan.mode} />
              <Field label="Plan updated" value={fmtDate(plan.updatedAt)} />
            </div>
            {plan.coachNote && (
              <div
                style={{
                  marginTop: 12,
                  background: "#1E4A35",
                  color: "#F5F0E8",
                  borderRadius: 8,
                  padding: "10px 14px",
                }}
              >
                <p className="mnf-eyebrow" style={{ color: "#E5B997", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700 }}>
                  Coach note
                </p>
                <p className="mnf-body" style={{ fontSize: 11.5, color: "#F5F0E8", marginTop: 4, lineHeight: 1.5, fontStyle: "italic" }}>
                  "{plan.coachNote}"
                </p>
              </div>
            )}
          </>
        ) : (
          <p className="mnf-body" style={{ fontSize: 11.5, color: "#6B7B6F", fontStyle: "italic" }}>
            No plan set yet. The sister has not yet onboarded macro targets.
          </p>
        )}
      </section>

      {/* 14-day adherence summary */}
      <section data-print-keep style={{ marginBottom: 20 }}>
        <h2 className="mnf-serif" style={{ fontSize: 14, fontWeight: 700, color: "#163828", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
          Last 14 days, at a glance
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 12 }}>
          <SummaryCell label="Days logged" value={`${summary.daysLogged} / 14`} />
          <SummaryCell label="Avg energy" value={`${summary.avgKcal} kcal`} />
          <SummaryCell
            label="Avg vs target"
            value={summary.target === 0 ? "no plan" : `${summary.avgKcalPctOfTarget}%`}
          />
          <SummaryCell
            label="In target band"
            value={summary.target === 0 ? "no plan" : `${summary.onTargetDays} days`}
          />
        </div>
        <p className="mnf-body" style={{ fontSize: 10, color: "#6B7B6F", lineHeight: 1.5, fontStyle: "italic", marginBottom: 8 }}>
          Target band is plus or minus 15 percent of the daily kcal target. Days with no logged meals are excluded
          from the average so you see what was actually consumed on logged days, not artificial zeros.
        </p>
      </section>

      {/* 14-day daily table */}
      <section data-print-keep style={{ marginBottom: 20 }}>
        <h2 className="mnf-serif" style={{ fontSize: 14, fontWeight: 700, color: "#163828", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
          Daily totals
        </h2>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 10.5,
            color: "#2D3E30",
          }}
          data-testid="table-daily-totals"
        >
          <thead>
            <tr style={{ background: "#1E4A35", color: "#F5F0E8" }}>
              <th style={th}>Day</th>
              <th style={th}>Date</th>
              <th style={thRight}>kcal</th>
              <th style={thRight}>Protein g</th>
              <th style={thRight}>Carbs g</th>
              <th style={thRight}>Fat g</th>
              <th style={thRight}>vs target</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d, i) => {
              const pct = summary.target === 0 || d.kcal === 0 ? null : Math.round((d.kcal / summary.target) * 100);
              const inBand = pct != null && pct >= 85 && pct <= 115;
              const noLog = d.kcal === 0;
              return (
                <tr
                  key={d.date}
                  data-print-row
                  style={{
                    background: i % 2 === 0 ? "#ffffff" : "#FAF6EE",
                    borderBottom: "1px solid rgba(30,74,53,0.06)",
                  }}
                >
                  <td style={td}>{dayOfWeekLabel(d.date)}</td>
                  <td style={td}>{fmtDateShort(d.date)}</td>
                  <td style={tdRight}>{noLog ? "no log" : d.kcal.toLocaleString()}</td>
                  <td style={tdRight}>{noLog ? "" : d.proteinG}</td>
                  <td style={tdRight}>{noLog ? "" : d.carbsG}</td>
                  <td style={tdRight}>{noLog ? "" : d.fatG}</td>
                  <td style={{
                    ...tdRight,
                    color: noLog ? "#6B7B6F" : inBand ? "#2C6248" : "#C0784A",
                    fontWeight: 700,
                  }}>
                    {pct == null ? (noLog ? "" : "n/a") : `${pct}%`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {/* Season micronutrient panel */}
      {panel && (
        <section data-print-keep data-print-newpage={panel.items.length > 4 ? "" : undefined} style={{ marginBottom: 20 }}>
          <h2 className="mnf-serif" style={{ fontSize: 14, fontWeight: 700, color: "#163828", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
            Season-specific micronutrient priorities
          </h2>
          <p className="mnf-body" style={{ fontSize: 11, color: "#6B7B6F", marginBottom: 12, lineHeight: 1.5 }}>
            {panel.subtitle}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {panel.items.map((item) => (
              <div
                key={item.name}
                data-print-row
                style={{
                  border: "1px solid rgba(30,74,53,0.10)",
                  borderRadius: 8,
                  padding: "10px 12px",
                  background: "#ffffff",
                }}
              >
                <div className="flex items-baseline gap-3" style={{ flexWrap: "wrap" }}>
                  <p className="mnf-serif" style={{ fontSize: 13, fontWeight: 700, color: "#163828" }}>
                    {item.name}
                  </p>
                  <p className="mnf-body" style={{ fontSize: 11, color: "#A07820", fontWeight: 700 }}>
                    {item.targetLabel}
                  </p>
                </div>
                <p className="mnf-body" style={{ fontSize: 10.5, color: "#2D3E30", marginTop: 4, lineHeight: 1.5 }}>
                  {item.why}
                </p>
                <p className="mnf-body" style={{ fontSize: 10, color: "#6B7B6F", marginTop: 4, lineHeight: 1.45 }}>
                  Sources: {item.sources.join(", ")}
                  {item.pairing ? `. ${item.pairing}` : ""}
                </p>
              </div>
            ))}
          </div>
          <p className="mnf-body" style={{ fontSize: 10, color: "#6B7B6F", marginTop: 10, fontStyle: "italic", lineHeight: 1.5 }}>
            {panel.footer}
          </p>
        </section>
      )}

      {/* Footer disclaimer + scripture */}
      <footer
        data-print-keep
        style={{
          marginTop: 24,
          paddingTop: 16,
          borderTop: "2px solid #1E4A35",
        }}
      >
        <p
          className="mnf-serif italic"
          style={{ fontSize: 12, color: "#163828", lineHeight: 1.55, textAlign: "center" }}
        >
          "She is clothed with strength and dignity, and she laughs without fear of the future."
        </p>
        <p
          className="mnf-eyebrow"
          style={{ textAlign: "center", color: "#A07820", marginTop: 4, fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700 }}
        >
          Proverbs 31:25
        </p>
        <div style={{ background: "#FAF6EE", border: "1px solid rgba(30,74,53,0.10)", borderRadius: 8, padding: "12px 14px", marginTop: 14 }}>
          <p className="mnf-body" style={{ fontSize: 10, color: "#2D3E30", lineHeight: 1.55 }}>
            <strong>Wellness, not prescription.</strong> Move Nourish Flourish coaching is delivered under a
            certified pre and postnatal coach scope of practice. Macro guidance is a wellness tool and is not
            intended to diagnose, treat, cure, or prevent any condition. Please direct any clinical questions,
            medication interactions, or symptoms outside this summary to {user.name}'s OB, midwife, or registered
            dietitian.
          </p>
        </div>
        <p className="mnf-body" style={{ fontSize: 9.5, color: "#6B7B6F", marginTop: 12, textAlign: "center", letterSpacing: "0.04em" }}>
          movenourishflourish.app · summary generated {fmtDate(generatedAt, { month: "short", day: "numeric", year: "numeric" })}
        </p>
      </footer>
    </div>
  );
}

/* ---------------------------- Field cells ---------------------------- */

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="mnf-eyebrow" style={{ color: "#6B7B6F", fontSize: 9, letterSpacing: "0.10em", textTransform: "uppercase", fontWeight: 700 }}>
        {label}
      </p>
      <p className="mnf-body" style={{ fontSize: 12, color: "#163828", marginTop: 2, fontWeight: 500 }}>
        {value}
      </p>
    </div>
  );
}

function TargetCell({ label, value, unit, accent }: { label: string; value: string; unit: string; accent: string }) {
  return (
    <div
      style={{
        border: "1px solid rgba(30,74,53,0.12)",
        borderRadius: 10,
        padding: "10px 12px",
        textAlign: "center",
        background: "#ffffff",
      }}
    >
      <p className="mnf-eyebrow" style={{ color: accent, fontSize: 9, letterSpacing: "0.10em", textTransform: "uppercase", fontWeight: 700 }}>
        {label}
      </p>
      <p className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "#163828", marginTop: 4, lineHeight: 1 }}>
        {value}
        <span className="mnf-body" style={{ fontSize: 11, color: "#6B7B6F", marginLeft: 3, fontWeight: 500 }}>{unit}</span>
      </p>
    </div>
  );
}

function SummaryCell({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: "#FAF6EE",
        border: "1px solid rgba(30,74,53,0.10)",
        borderRadius: 8,
        padding: "10px 12px",
      }}
    >
      <p className="mnf-eyebrow" style={{ color: "#6B7B6F", fontSize: 9, letterSpacing: "0.10em", textTransform: "uppercase", fontWeight: 700 }}>
        {label}
      </p>
      <p className="mnf-serif" style={{ fontSize: 16, fontWeight: 500, color: "#163828", marginTop: 4 }}>
        {value}
      </p>
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
};
const thRight: React.CSSProperties = { ...th, textAlign: "right" };
const td: React.CSSProperties = { padding: "7px 10px", fontSize: 10.5 };
const tdRight: React.CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

/* ---------------------------- Page wrapper (screen) ---------------------------- */

export default function ProviderExport() {
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account: this is a health-data export document.
  // Handing Hannah's data to a sister's provider would be a serious privacy
  // breach, not just a display bug.
  const userId = me?.id ?? null;

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Loading your account before we can build this export...
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--mnf-cr, #F5F0E8)",
        padding: "16px 0 32px",
      }}
    >
      {/* Top bar visible on screen, hidden on print */}
      <div
        data-print-hide
        style={{
          padding: "0 16px",
          marginBottom: 16,
        }}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link href="/macros" className="no-underline" data-testid="link-back-macros">
            <span
              className="inline-flex items-center gap-1.5 mnf-body hover-elevate transition-all"
              style={{
                padding: "8px 12px",
                borderRadius: 999,
                background: "rgba(30,74,53,0.06)",
                color: "var(--mnf-f)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              <Icon name="arrow_back" size={14} /> Back
            </span>
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            <PillTag tone="fiber">Wellness summary</PillTag>
            <PillTag tone="gold">Print ready</PillTag>
          </div>
        </div>
      </div>

      {/* Top instructions card, hidden on print */}
      <div data-print-hide style={{ padding: "0 16px", marginBottom: 16 }}>
        <GlassCard organic padding={16} data-testid="card-export-instructions">
          <div className="flex items-start gap-3">
            <span style={{ fontSize: 26, lineHeight: 1 }} aria-hidden>📄</span>
            <div className="flex-1">
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-purple)" }}>Share with my provider</p>
              <p
                className="mnf-serif"
                style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)", marginTop: 4 }}
              >
                A clean summary you can hand your OB, midwife, or RD
              </p>
              <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", marginTop: 6, lineHeight: 1.55 }}>
                Tap "Save as PDF" to open your device's print dialog, then choose Save as PDF (or print
                directly). Only the document below will appear, never the app navigation.
              </p>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Btn
                  variant="primary"
                  size="md"
                  onClick={() => {
                    if (typeof window !== "undefined" && typeof window.print === "function") {
                      window.print();
                    }
                  }}
                  data-testid="button-print-export"
                >
                  <Icon name="print" size={14} /> Save as PDF
                </Btn>
                <Btn
                  variant="ghost"
                  size="md"
                  onClick={() => {
                    if (typeof window !== "undefined" && typeof window.print === "function") {
                      window.print();
                    }
                  }}
                  data-testid="button-print-paper"
                >
                  <Icon name="local_printshop" size={14} /> Print on paper
                </Btn>
              </div>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* The actual document. Visible on screen, the only thing on print. */}
      <div style={{ padding: "0 16px" }}>
        <ProviderDocument userId={userId} />
      </div>

      {/* Bottom helper, hidden on print */}
      <div data-print-hide style={{ padding: "16px 16px 0" }}>
        <p
          className="mnf-body italic"
          style={{ fontSize: 11.5, color: "var(--mnf-tg)", textAlign: "center", lineHeight: 1.5 }}
        >
          Always defer to your clinician. This summary is a wellness companion, not medical advice.
        </p>
      </div>
    </div>
  );
}
